/**
 * ReviewCompletionStatsCard — Board card showing % of concluded reviews that
 * were fully checklisted + signed, indicating audit-grade compliance quality.
 *
 * Data source: GET /api/tasks/reviews/stats
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ClipboardCheck, ShieldCheck, Loader2 } from 'lucide-react';

const MODULE_LABELS = {
  policy: 'Políticas',
  supplier: 'Fornecedores',
  dpia: 'DPIAs',
  audit: 'Auditorias',
  bcm: 'BCM',
};

export function ReviewCompletionStatsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/tasks/reviews/stats');
        if (!ignore) setData(res.data);
      } catch {
        if (!ignore) toast.error('Falha ao carregar estatísticas de revisão.');
      } finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, []);

  return (
    <Card data-testid="review-completion-stats" className={
      data && data.total_completed_reviews > 0 && (data.checklist_completion_rate_pct < 80 || data.signed_rate_pct < 90)
        ? 'border-amber-300'
        : ''
    }>
      <CardHeader className="border-b pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-emerald-600" /> Qualidade de Revisões
          </CardTitle>
          {data && data.total_completed_reviews > 0 && (data.checklist_completion_rate_pct < 80 || data.signed_rate_pct < 90) && (
            <Badge variant="secondary" className="bg-amber-50 text-amber-800 border-amber-300" data-testid="review-quality-alert">
              ⚠ Atenção
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
        ) : !data || data.total_completed_reviews === 0 ? (
          <div className="text-sm text-slate-400 text-center py-4">Sem revisões concluídas ainda.</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Concluídas</div>
                <div className="text-2xl font-bold text-slate-900 tabular-nums" data-testid="stats-total">{data.total_completed_reviews}</div>
              </div>
              <div className="rounded-lg border border-emerald-200 p-3 bg-emerald-50">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold mb-1">Checklist completa</div>
                <div className="text-2xl font-bold text-emerald-800 tabular-nums" data-testid="stats-full-checklist">
                  {data.with_full_checklist}
                  <span className="text-sm text-emerald-600 ml-1">({data.checklist_completion_rate_pct}%)</span>
                </div>
              </div>
              <div className="rounded-lg border border-indigo-200 p-3 bg-indigo-50">
                <div className="text-[10px] uppercase tracking-widest text-indigo-700 font-semibold mb-1">Assinadas</div>
                <div className="text-2xl font-bold text-indigo-800 tabular-nums" data-testid="stats-signed">
                  {data.signed}
                  <span className="text-sm text-indigo-600 ml-1">({data.signed_rate_pct}%)</span>
                </div>
              </div>
            </div>
            {/* Per-module */}
            {Object.keys(data.by_module || {}).length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Por módulo</div>
                <div className="space-y-1.5">
                  {Object.entries(data.by_module).map(([mod, m]) => {
                    const pct = m.total ? Math.round((m.full_checklist / m.total) * 100) : 0;
                    return (
                      <div key={mod} className="flex items-center gap-2 text-xs" data-testid={`stats-module-${mod}`}>
                        <span className="w-24 text-slate-600 font-medium">{MODULE_LABELS[mod] || mod}</span>
                        <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                          <div
                            className="h-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="tabular-nums text-slate-700 font-medium w-16 text-right">
                          {m.full_checklist}/{m.total} ({pct}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="pt-2 border-t border-slate-100 flex items-center gap-2 text-[11px] text-slate-500">
              <ShieldCheck className="h-3 w-3 text-emerald-600" />
              <span>Métrica auditável: cada revisão contém <strong>checklist</strong>, <strong>aprovador</strong> e (para políticas) <strong>snapshot</strong> preservado.</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default ReviewCompletionStatsCard;
